'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { signIn } from 'next-auth/react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { DatePickerBirth } from '@/components/ui/date-picker-birth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.48 0-1.87-.8-3.64-.8-1.77 0-2 .74-3.63.74-1.55 0-2.37-.88-3.43-2.62-1.87-2.86-2.63-7.15-1.03-10.49 1.03-2.2 3.29-3.62 5.43-3.66 1.85-.08 3.39 1.08 3.86 1.08.46 0 2.22-1.35 4.28-1.35.91 0 3.73.35 5.52 3.28-.04.01-3.61 2.11-3.61 6.39z"
    />
  </svg>
);

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function SignUpPage() {
  const t = useTranslations('auth');
  const locale = useLocale();
  const router = useRouter();
  const [formData, setFormData] = useState({
    firstName: '',
    name: '',
    dateOfBirth: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptedTerms: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName || formData.firstName.length < 2) {
      setError('Le prénom est requis et doit contenir au moins 2 caractères');
      return;
    }

    if (!formData.name || formData.name.length < 2) {
      setError('Le nom est requis et doit contenir au moins 2 caractères');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!formData.acceptedTerms) {
      setError('Vous devez accepter les CGU / CGV pour créer un compte');
      return;
    }

    // Validation de la date de naissance si fournie
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
        ? age - 1 
        : age;
      
      if (actualAge < 18) {
        setError('Vous devez avoir au moins 18 ans pour créer un compte');
        return;
      }
      
      if (birthDate > today) {
        setError('La date de naissance ne peut pas être dans le futur');
        return;
      }
    }

    setIsLoading(true);

    try {
      // Créer le compte
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          name: formData.name,
          dateOfBirth: formData.dateOfBirth || undefined,
          acceptedTerms: formData.acceptedTerms,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Afficher le message d'erreur du serveur ou un message par défaut
        const errorMessage = data.error || data.message || 'Une erreur est survenue lors de l\'inscription';
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      const normalizedEmail = formData.email.toLowerCase().trim();
      sessionStorage.setItem('pending_signup_password', formData.password);
      router.push(`/auth/verify-email?email=${encodeURIComponent(normalizedEmail)}`);
      router.refresh();
    } catch (err) {
      setError('Une erreur est survenue lors de l\'inscription');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-poker-darker via-poker-dark to-poker-darker flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="light" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('signUp.title')}
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            {t('signUp.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.firstName')}
              </label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full"
                placeholder={t('signUp.firstNamePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.lastName')}
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full"
                placeholder={t('signUp.lastNamePlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.dateOfBirth')} <span className="text-gray-400">({t('signUp.optional')})</span>
              </label>
              <DatePickerBirth
                value={formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T12:00:00') : undefined}
                onChange={(date) => {
                  if (date) {
                    // Formater la date en YYYY-MM-DD sans problème de fuseau horaire
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const dateString = `${year}-${month}-${day}`;
                    setFormData({
                      ...formData,
                      dateOfBirth: dateString,
                    });
                  } else {
                    setFormData({
                      ...formData,
                      dateOfBirth: '',
                    });
                  }
                  setError('');
                }}
                placeholder={t('signUp.dateOfBirthPlaceholder')}
                maxDate={React.useMemo(() => {
                  const today = new Date();
                  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                  return maxDate;
                }, [])}
                className="w-full"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('signUp.dateOfBirthHint')}
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.email')}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full"
                placeholder={t('signUp.emailPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.password')}
              </label>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="w-full"
                placeholder={t('signUp.passwordPlaceholder')}
                minLength={8}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('signUp.passwordHint')}
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('signUp.confirmPassword')}
              </label>
              <PasswordInput
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full"
                placeholder={t('signUp.confirmPasswordPlaceholder')}
              />
            </div>

            <label
              htmlFor="acceptedTerms"
              className="flex items-start gap-2 rounded-md border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-300"
            >
              <input
                id="acceptedTerms"
                name="acceptedTerms"
                type="checkbox"
                checked={formData.acceptedTerms}
                onChange={handleChange}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
              />
              <span>
                {t('signUp.acceptTermsPrefix')}{' '}
                <Link href={`/${locale}/terms`} className="text-gold underline hover:text-gold-dark">
                  CGU
                </Link>
                {' / '}
                <Link href={`/${locale}/cgv`} className="text-gold underline hover:text-gold-dark">
                  CGV
                </Link>
                .
              </span>
            </label>

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('signUp.loading')}
                </>
              ) : (
                t('signUp.submit')
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">
                  {t('signUp.or')}
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => signIn('google', { callbackUrl: '/dashboard' })}
            >
              <GoogleIcon />
              <span className="ml-2">{t('signUp.continueWithGoogle')}</span>
            </Button>

            {process.env.NEXT_PUBLIC_APPLE_SIGNIN_ENABLED === '1' ? (
              <Button
                type="button"
                className="mt-2 w-full bg-black text-white hover:bg-black/90 border border-black dark:bg-white dark:text-black dark:border-white dark:hover:bg-white/90"
                onClick={() => signIn('apple', { callbackUrl: '/dashboard' })}
              >
                <AppleIcon />
                <span className="ml-2">{t('signUp.continueWithApple')}</span>
              </Button>
            ) : null}

            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              {t('signUp.hasAccount')}{' '}
              <a
                href="/auth/signin"
                className="text-gold hover:text-gold-dark font-medium"
              >
                {t('signUp.signIn')}
              </a>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

